import { useEffect } from 'react';
import { useZones } from 'features/Responder/features/Zones';
import { centroid, normalizePoint } from 'features/Responder/features/Zones';
import { deriveCheckpointsFromTour, ensureLatLng } from 'features/Responder/features/Tours/utils';
import normalizeTravelMode from 'features/Responder/features/Tours/features/TourBuilder/utils/normalizeTravelMode';

/**
 * Hydrate builder state when an existing tour is selected for editing.
 * @param {object} params
 * @param {function} params.setIsLooped
 */
export function useBuilderHydration({
  builderTourData,
  builderHydrationRef,
  setStartLocation,
  setStops,
  setRouteData,
  setEditingTourId,
  setSaveSuccess,
  setSaveError,
  setSelectedCheckpointId,
  setIsLooped,
}) {
  let zonesWithMeta = [];
  try {
    const zonesCtx = useZones();
    zonesWithMeta = zonesCtx?.zonesWithMeta || zonesCtx?.zones || [];
  } catch (err) {
    zonesWithMeta = [];
  }

  const resolveZoneInfo = (zoneId) => {
    if (zoneId == null) return null;
    const zone = zonesWithMeta.find((z) => `${z.id}` === `${zoneId}`);
    if (!zone) return null;
    const raw =
      zone.polygon ?? zone.geometry?.points ?? zone.geometry?.coord ?? zone._points ?? [];
    const points = Array.isArray(raw) ? raw.map((pt) => normalizePoint(pt)).filter(Boolean) : [];
    const boundsCenter =
      zone?.bounds &&
      Number.isFinite(zone.bounds.north) &&
      Number.isFinite(zone.bounds.south) &&
      Number.isFinite(zone.bounds.east) &&
      Number.isFinite(zone.bounds.west)
        ? {
            lat: (zone.bounds.north + zone.bounds.south) / 2,
            lng: (zone.bounds.east + zone.bounds.west) / 2,
          }
        : null;
    const center = centroid(points) || boundsCenter || points[0] || null;
    const geometry = points.length
      ? {
          type: 'zone',
          points,
          coord: points,
          isZone: true,
          zoneId: zone.id,
        }
      : null;
    return { zone, center, geometry };
  };

  const resolveGeometryCenter = (geometry) => {
    if (!geometry) return null;
    const raw = geometry.points ?? geometry.coord ?? geometry.polygon ?? [];
    const points = Array.isArray(raw) ? raw.map((pt) => normalizePoint(pt)).filter(Boolean) : [];
    return centroid(points) || points[0] || null;
  };
  useEffect(() => {
    if (!builderTourData) return;
    if (!builderHydrationRef?.current) return;
    if (`${builderHydrationRef.current}` !== `${builderTourData.id ?? ''}`) return;
    const {
      originNode,
      checkpoints: normalizedCheckpoints,
      isLooped,
    } = deriveCheckpointsFromTour(builderTourData);

    const { lat: originLat, lng: originLng } = ensureLatLng(originNode);
    const originZoneId = originNode?.zoneId ?? originNode?.geometry?.zoneId ?? null;
    const originZoneInfo = resolveZoneInfo(originZoneId);
    const originGeometryCenter = resolveGeometryCenter(originNode?.geometry);
    const resolvedOriginLat =
      Number.isFinite(originLat) ? originLat : originGeometryCenter?.lat ?? originZoneInfo?.center?.lat;
    const resolvedOriginLng =
      Number.isFinite(originLng) ? originLng : originGeometryCenter?.lng ?? originZoneInfo?.center?.lng;

    const originGeometry = originNode?.geometry ?? originZoneInfo?.geometry ?? null;
    const loopReturnMode = (() => {
      if (!isLooped) return 'DRIVING';
      const legs = Array.isArray(builderTourData?.legs) ? builderTourData.legs : [];
      const lastLegFromLegs = legs.length ? legs[legs.length - 1] : null;
      const nodes = Array.isArray(builderTourData?.nodes) ? builderTourData.nodes : [];
      const lastNode = nodes.length ? nodes[nodes.length - 1] : null;
      return normalizeTravelMode(
        lastLegFromLegs?.travelMode ||
          lastLegFromLegs?.mode ||
          lastNode?.leg?.travelMode ||
          lastNode?.travelMode ||
          lastNode?.mode,
      );
    })();

    if (Number.isFinite(resolvedOriginLat) && Number.isFinite(resolvedOriginLng)) {
      const zoneId = originZoneId ?? originZoneInfo?.zone?.id ?? null;
      setStartLocation((prev) => ({
        ...prev,
        address: originNode?.address || originZoneInfo?.zone?.name || prev.address,
        lat: resolvedOriginLat ?? '',
        lng: resolvedOriginLng ?? '',
        placeId: originNode?.placeId || prev.placeId,
        id: originNode?.id || prev.id || undefined,
        useCurrent: false,
        zoneId: zoneId,
        geometry: originGeometry,
        returnMode: loopReturnMode,
      }));
    } else if (originZoneId != null) {
      const zoneId = originZoneId ?? originZoneInfo?.zone?.id ?? null;
      setStartLocation((prev) => ({
        ...prev,
        address: originNode?.address || originZoneInfo?.zone?.name || prev.address,
        placeId: originNode?.placeId || prev.placeId,
        id: originNode?.id || prev.id || undefined,
        useCurrent: false,
        zoneId: zoneId,
        geometry: originGeometry ?? prev.geometry,
        returnMode: loopReturnMode,
      }));
    }

    const checkpointsWithModes = normalizedCheckpoints.map((cp) => {
      const { lat, lng } = ensureLatLng(cp);
      const cpZoneId = cp?.zoneId ?? cp?.geometry?.zoneId ?? null;
      const cpZoneInfo = resolveZoneInfo(cpZoneId);
      const cpGeometryCenter = resolveGeometryCenter(cp?.geometry);
      const resolvedLat = Number.isFinite(lat) ? lat : cpGeometryCenter?.lat ?? cpZoneInfo?.center?.lat;
      const resolvedLng = Number.isFinite(lng) ? lng : cpGeometryCenter?.lng ?? cpZoneInfo?.center?.lng;
      return {
        ...cp,
        lat: resolvedLat ?? cp.lat ?? '',
        lng: resolvedLng ?? cp.lng ?? '',
        geometry: cp?.geometry ?? cpZoneInfo?.geometry ?? null,
        zoneId: cpZoneId ?? cp?.zoneId ?? null,
        mode: cp.mode || cp.travelMode || 'DRIVING',
      };
    });

    setStops(checkpointsWithModes);

    const legsFromNodes = Array.isArray(builderTourData?.nodes)
      ? builderTourData.nodes
          .map((node, idx) => {
            if (idx === 0) return null;
            return {
              travelMode:
                node?.leg?.travelMode || node.travelMode || node.mode || 'DRIVING',
              duration: node?.leg?.durationSeconds ?? node.duration ?? null,
              distanceMeters: node?.leg?.distanceMeters ?? node.distanceMeters ?? null,
              polyline:
                node?.leg?.encodedPolyline || node.encodedPolyline || node.polyline || null,
            };
          })
          .filter(Boolean)
      : [];

    const hydratedLegs =
      Array.isArray(builderTourData?.legs) && builderTourData.legs.length
        ? builderTourData.legs
        : legsFromNodes;

    setRouteData({
      polyline: builderTourData.encodedPolyline || builderTourData.polyline || null,
      legs: hydratedLegs,
    });
    if (typeof setIsLooped === 'function') {
      const explicitLoop =
        typeof builderTourData?.isLooped === 'boolean' ? builderTourData.isLooped : null;
      setIsLooped(explicitLoop ?? isLooped ?? false);
    }
    setEditingTourId(builderTourData.id || null);
    setSaveSuccess('');
    setSaveError('');
    setSelectedCheckpointId(null);
    builderHydrationRef.current = null;
  }, [
    builderTourData,
    builderHydrationRef,
    zonesWithMeta,
    setEditingTourId,
    setRouteData,
    setSaveError,
    setSaveSuccess,
    setSelectedCheckpointId,
    setStartLocation,
    setStops,
  ]);
}


